import type { HairlineFigure } from "./Hairline";
import {
  Cam,
  clamp,
  facing,
  fit,
  hull,
  lerp,
  open,
  poly,
  prism,
  proj,
  ringAt,
  rings,
  rrect,
  run,
  seg,
  unproj,
} from "./iso";
import { tdone, tset, tval, tween } from "./motion";
import {
  disposer,
  mk,
  pointer,
  put,
  reflect,
  register,
  solid,
  type FigureEls,
  type FigureHandle,
  type Solid,
} from "./stage";

/**
 * Abacus: a soroban of six rods, a beam across them, one bead above the beam
 * and four below it on each rod. Where the pointer runs along a rod, its beads
 * part there: those before the finger slide to the far end and those after it
 * to the near end, crossing the gap one after another on the 700ms lift curve,
 * the bead at its edge first, so none ever passes through another.
 * A bead against the beam counts, five above it and one below; the beads a rod
 * counts take the bright stroke. At rest the frame holds a number, one bead
 * caught on its way to the beam. The slider is the stagger, in ms.
 *
 * The pattern: discrete items, as Riffle. Tweens, a stagger that spreads out
 * from the gap the beads cross, and a hit test on the plane of the beads'
 * tops, which never moves.
 */

const R = 6,
  BW = 9,
  BD = 14,
  BR = 4.4,
  G = 18;
/** Along a rod: the deck above the beam, the beam, and the deck below it, which holds four. */
const H = 23,
  BM = 6,
  E0 = H + BM,
  GE = 22,
  L = E0 + 4 * BW + GE;
const Z0 = 3,
  Z1 = 11,
  ZR = 7,
  FH = 9,
  FB = 7;
const X0 = -1 - FB,
  X1 = L + 1 + FB,
  Y0 = -BD / 2 - 2 - FB,
  Y1 = (R - 1) * G + BD / 2 + 2 + FB;
/** At rest: the number each rod counts, and the bead caught on its way to the beam (rod 4's fourth, 45% across). */
const REST = [2, 6, 3, 8, 0, 4],
  CAUGHT = { j: 3, i: 3, t: 0.45 };

/** Where a rod's beads sit when it counts v: the one above the beam, then the four below it. */
const at = (v) => {
  const h = v >= 5,
    e = v % 5;
  return [
    h ? H - BW : 0,
    ...[0, 1, 2, 3].map((i) => (i < e ? E0 + i * BW : L - (4 - i) * BW)),
  ];
};
const restAt = (j) => {
  const xs = at(REST[j]);
  if (j === CAUGHT.j)
    xs[CAUGHT.i + 1] = lerp(xs[CAUGHT.i + 1], E0 + CAUGHT.i * BW, CAUGHT.t);
  return xs;
};

/** A run of points ordered left to right on screen. */
const LR = (pts) =>
  pts[0][0] <= pts[pts.length - 1][0] ? pts : pts.slice().reverse();

/** The frame, which never moves, as Riffle's tray: `far` is painted before the rods, `near` after them. */
function frame(P, front, outer, inner) {
  const far = [
    [poly(hull(ringAt(P, outer, 0).concat(ringAt(P, outer, FH)))), "sil"],
    [poly(ringAt(P, inner, FH)), "nf"],
  ];
  const iF = LR(ringAt(P, run(inner, front), FH)),
    oT = LR(ringAt(P, run(outer, front), FH)),
    oB = LR(ringAt(P, run(outer, front), 0));
  const near = [
    [poly([...iF, oT[oT.length - 1], ...oB.slice().reverse(), oT[0]]), "fo"],
    [open(oT), "nf lo"],
    [open(iF), "nf"],
    [open([oT[0], ...oB, oT[oT.length - 1]]), "nf sil"],
  ];
  return { far, near };
}

function mount({ stage, svg, read }: FigureEls, value: number): FigureHandle {
  const bag = disposer();
  let stag = value;

  const C = Cam(45, 0.5, 1.86);
  fit(
    C,
    [
      [X0, Y0, 0],
      [X1, Y1, -10],
      [X1, Y0, 0],
      [X0, Y1, 0],
      [X0, Y0, Z1],
      [X1, Y0, Z1],
    ],
    200,
    166,
  );
  const P = proj(C),
    front = facing(C);
  const outer = rrect(X0, Y0, X1, Y1, 8, 6),
    inner = rrect(X0 + FB, Y0 + FB, X1 - FB, Y1 - FB, 2.5, 5);
  const paths = frame(P, front, outer, inner);

  const g = mk("g", {}, svg);
  reflect(svg, g, P, front, outer, 0, 14);
  for (const [d, cls] of paths.far) mk("path", { d, class: cls }, g);

  // Back to front: every rod's deck above the beam, the beam, then every rod's
  // deck below it, each rod from the far side. Beads never pass one another,
  // so this order holds in every pose.
  const rods = REST.map((v, j) => ({
    y: j * G,
    v,
    beads: restAt(j).map((x) => ({
      x: tween(x),
      to: x,
      el: null as Solid,
      drawn: NaN,
    })),
  }));
  const deck = (j, lo, hi, from, to) => {
    mk(
      "path",
      { d: seg(P(lo, rods[j].y, ZR), P(hi, rods[j].y, ZR)), class: "nf" },
      g,
    );
    for (let i = from; i < to; i++) rods[j].beads[i].el = solid(g);
  };
  rods.forEach((_, j) => deck(j, -1, H, 0, 1));
  const [br, bi] = rings(H, Y0 + FB, H + BM, Y1 - FB, 2, 1);
  put(solid(g), prism(P, front, br, bi, 0, FH));
  rods.forEach((_, j) => deck(j, E0, L + 1, 1, 5));

  for (const [d, cls] of paths.near) mk("path", { d, class: cls }, g);

  /** A bead whose tween hasn't moved keeps its paths: most of the 30 are still on most frames. */
  function drawBead(b, y, now) {
    const x = tval(b.x, now);
    if (x === b.drawn) return;
    b.drawn = x;
    const [ring, crease] = rings(x, y - BD / 2, x + BW, y + BD / 2, BR, 1.1);
    put(b.el, prism(P, front, ring, crease, Z0, Z1));
  }

  const B = register(stage, (_dt, now) => {
    let moving = false;
    for (const rod of rods)
      for (const b of rod.beads) {
        drawBead(b, rod.y, now);
        if (!tdone(b.x, now)) moving = true;
      }
    return moving;
  });
  bag.add(() => B.unregister());

  /** The bright: the beads the rod under the pointer counts; at rest, the bead caught on its way. */
  function light(j) {
    rods.forEach((rod, k) =>
      rod.beads.forEach((b, i) => {
        const counts = i === 0 ? rod.v >= 5 : i - 1 < rod.v % 5;
        b.el.sil.classList.toggle(
          "hi",
          j < 0 ? k === CAUGHT.j && i === CAUGHT.i + 1 : k === j && counts,
        );
      }),
    );
  }

  /**
   * Moves a rod's beads to xs. In each deck the beads that cross go one after
   * another, the one at the gap's edge first: the leader of those going left is
   * the leftmost, of those going right the rightmost.
   */
  function move(rod, xs) {
    const now = performance.now();
    for (const deck of [[0], [1, 2, 3, 4]]) {
      const left = deck.filter((i) => xs[i] < rod.beads[i].to),
        right = deck.filter((i) => xs[i] > rod.beads[i].to);
      for (const i of left)
        tset(rod.beads[i].x, xs[i], now, (i - left[0]) * stag);
      for (const i of right)
        tset(rod.beads[i].x, xs[i], now, (right[right.length - 1] - i) * stag);
      for (const i of deck) rod.beads[i].to = xs[i];
    }
  }

  let act = -1;
  /** The rod under a pointer on the plane of the beads' tops, and the number the finger leaves it counting. */
  function hit([sx, sy]) {
    const [wx, wy] = unproj(C, sx, sy, Z1),
      j = Math.round(wy / G);
    if (j < 0 || j >= R || wx < -BW || wx > L + BW) return null;
    const rod = rods[j],
      h = rod.v >= 5,
      e = rod.v % 5;
    if (wx < H + BM / 2) return { j, v: (wx < H / 2 ? 5 : 0) + e };
    const k = clamp(Math.round((wx - E0 - GE / 2) / BW), 0, 4);
    return { j, v: (h ? 5 : 0) + k };
  }

  function choose(pick) {
    if (!pick) {
      if (act < 0) return;
      act = -1;
      rods.forEach((rod, j) => {
        rod.v = REST[j];
        move(rod, restAt(j));
      });
      read.textContent = "rest";
    } else {
      const rod = rods[pick.j];
      act = pick.j;
      if (rod.v !== pick.v) {
        rod.v = pick.v;
        move(rod, at(pick.v));
      }
      read.textContent = `rod ${pick.j + 1} · ${pick.v}`;
    }
    light(act);
    B.wake();
  }

  light(-1);
  bag.add(
    pointer(stage, { move: (p) => choose(hit(p)), leave: () => choose(null) }),
  );
  bag.add(() => svg.replaceChildren());

  return {
    set: (v) => {
      stag = v;
    },
    destroy: () => bag.dispose(),
  };
}

export const ABACUS: HairlineFigure = {
  name: "abacus",
  means:
    "A soroban of six rods: where the pointer runs along a rod, its beads part at the finger, and the beads the rod counts light up.",
  range: [0, 45, 90],
  tour: [[218, 193], [199, 113], [195, 159], null],
  mount,
};
